/* eslint-disable react-refresh/only-export-components */
import { useEffect } from 'react';
import { absoluteUrl, SITE_NAME } from './site';

type JsonLd = Record<string, unknown> | Record<string, unknown>[];

type SeoProps = {
  title: string;
  description: string;
  path?: string;
  image?: string;
  imageAlt?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  jsonLd?: JsonLd;
};

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let node = document.head.querySelector<HTMLMetaElement>(selector);
  if (!node) {
    node = document.createElement('meta');
    document.head.appendChild(node);
  }
  Object.entries(attributes).forEach(([key, value]) => node?.setAttribute(key, value));
}

function removeMeta(selector: string) {
  document.head.querySelector(selector)?.remove();
}

function upsertLink(rel: string, href: string, attributes: Record<string, string> = {}) {
  const selectorParts = [`link[rel="${rel}"]`];
  if (attributes.hreflang) selectorParts.push(`[hreflang="${attributes.hreflang}"]`);
  let node = document.head.querySelector<HTMLLinkElement>(selectorParts.join(''));
  if (!node) {
    node = document.createElement('link');
    node.rel = rel;
    document.head.appendChild(node);
  }
  node.href = href;
  Object.entries(attributes).forEach(([key, value]) => node?.setAttribute(key, value));
}

export function Seo({
  title,
  description,
  path = '/',
  image = '/og-nexhance-ai.png',
  imageAlt = 'Nexhance AI image enhancement, upscaling and photo restoration studio',
  type = 'website',
  noindex = false,
  publishedTime,
  modifiedTime,
  jsonLd
}: SeoProps) {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonical = absoluteUrl(path);
    const socialImage = absoluteUrl(image);

    document.title = fullTitle;
    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: noindex ? 'noindex,follow' : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
    });
    upsertMeta('meta[name="googlebot"]', {
      name: 'googlebot',
      content: noindex ? 'noindex,follow' : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
    });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'en_US' });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: socialImage });
    upsertMeta('meta[property="og:image:secure_url"]', { property: 'og:image:secure_url', content: socialImage });
    upsertMeta('meta[property="og:image:type"]', { property: 'og:image:type', content: 'image/png' });
    upsertMeta('meta[property="og:image:width"]', { property: 'og:image:width', content: '1200' });
    upsertMeta('meta[property="og:image:height"]', { property: 'og:image:height', content: '630' });
    upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: imageAlt });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: socialImage });
    upsertMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: imageAlt });

    if (type === 'article' && publishedTime) {
      upsertMeta('meta[property="article:published_time"]', { property: 'article:published_time', content: publishedTime });
    } else {
      removeMeta('meta[property="article:published_time"]');
    }
    if (type === 'article' && modifiedTime) {
      upsertMeta('meta[property="article:modified_time"]', { property: 'article:modified_time', content: modifiedTime });
    } else {
      removeMeta('meta[property="article:modified_time"]');
    }

    upsertLink('canonical', canonical);
    upsertLink('alternate', canonical, { hreflang: 'en' });
    upsertLink('alternate', canonical, { hreflang: 'x-default' });

    document.head.querySelectorAll('script[data-nexhance-server-seo="jsonld"], script[data-nexhance-seo="jsonld"]').forEach((node) => node.remove());
    document.getElementById('nexhance-seo-fallback-style')?.remove();
    if (jsonLd) {
      const entries = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      entries.forEach((entry) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.dataset.nexhanceSeo = 'jsonld';
        script.text = JSON.stringify(entry);
        document.head.appendChild(script);
      });
    }
  }, [description, image, imageAlt, jsonLd, modifiedTime, noindex, path, publishedTime, title, type]);

  return null;
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path)
    }))
  };
}
